import { AlertTriangle, Inbox, SearchX } from "lucide-react";
import { useT } from "@/i18n";
import { Button } from "../button";

/**
 * The four designed states every screen must cover. Shared components, not per-screen
 * improvisations — see `.claude/docs/ui-patterns.md`.
 *
 * `title` and `description` arrive as props — already translated by the screen, which is the
 * only place that knows what it is empty *of*. What lives here is the copy that belongs to the
 * pattern rather than to a screen: the no-result phrasing, the retry, and the diagnostic
 * reference every error state is required to show.
 */

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-border flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center">
      {children}
    </div>
  );
}

/** Shown for any wait. Structure with placeholders, never a blank region. */
export function LoadingState({ rows = 5, label }: { rows?: number; label?: string }) {
  const t = useT();

  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label ?? t("common.loading")}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="bg-muted h-11 animate-pulse rounded-md" />
      ))}
    </div>
  );
}

/** Nothing exists yet. Explains the screen's purpose and offers the creating action. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <Frame>
      <Inbox className="text-muted-foreground h-8 w-8" aria-hidden />
      <div className="space-y-1">
        <h2 className="font-medium">{title}</h2>
        <p className="text-muted-foreground max-w-sm text-sm">{description}</p>
      </div>
      {action && <Button onClick={action.onClick}>{action.label}</Button>}
    </Frame>
  );
}

/**
 * Filters exclude everything. Names the active filters and offers reset inline — a
 * generic "nothing found" leaves the user guessing why.
 */
export function NoResultState({
  activeFilters,
  onClear,
  scope,
}: {
  activeFilters: string[];
  onClear: () => void;
  /**
   * What the view is scoped to beyond its filters — the pipeline's year. Named because
   * clearing every filter would still show nothing if the year is the reason, and the
   * reader would be left clearing filters that were never the problem.
   */
  scope?: string;
}) {
  const t = useT();

  return (
    <Frame>
      <SearchX className="text-muted-foreground h-8 w-8" aria-hidden />
      <div className="space-y-1">
        {/* Two whole sentences rather than a base plus an appended clause: where the scope
            goes in the sentence is not the same in every language. */}
        <h2 className="font-medium">
          {scope ? t("state.noResultTitleScoped", scope) : t("state.noResultTitle")}
        </h2>
        {activeFilters.length > 0 && (
          <p className="text-muted-foreground max-w-md text-sm">
            {t("state.filteringBy", activeFilters.join(", "))}
          </p>
        )}
      </div>
      <Button variant="outline" onClick={onClear}>
        {t("state.clearFilters")}
      </Button>
    </Frame>
  );
}

/**
 * An operation failed. Says what failed, offers a retry, and **always** shows the
 * diagnostic identifier — `requestId` is required, so no screen can forget it and
 * leave support with nothing to search for.
 */
export function ErrorState({
  title,
  message,
  requestId,
  onRetry,
  action,
}: {
  title?: string;
  message: string;
  requestId: string;
  onRetry?: () => void;
  /**
   * A way out when retrying cannot help — a slug that names nothing will name nothing on
   * the second attempt too, so "Try again" is the wrong and only offer.
   */
  action?: { label: string; onClick: () => void };
}) {
  const t = useT();

  return (
    <Frame>
      <AlertTriangle className="text-destructive h-8 w-8" aria-hidden />
      <div className="space-y-1">
        <h2 className="font-medium">{title ?? t("state.errorTitle")}</h2>
        <p className="text-muted-foreground max-w-md text-sm">{message}</p>
      </div>
      {action && (
        <Button variant="outline" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
      {onRetry && (
        <Button variant="outline" onClick={onRetry}>
          {t("common.tryAgain")}
        </Button>
      )}
      <p className="text-muted-foreground text-xs">
        {t("state.reference")} <code className="bg-muted rounded px-1 py-0.5 font-mono">{requestId}</code> —{" "}
        {t("state.referenceHint")}
      </p>
    </Frame>
  );
}
